import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Button, Theme, SxProps } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useSearchParams } from 'react-router-dom';
import CustomButton, { ICustomButton } from '../Button/CustomButton';
import { StyledBadge } from '../StyledBadge/StyledBadge';

interface ICustomSegmentedControl {
    onHandleClick: (tab: string, index: number) => void;
    tabs: string[];
    /** CastingNoir-style: transparent bar, bottom border, highlight underline + text */
    variant?: 'default' | 'underline';
    /** Keeps highlight in sync when tab is controlled externally (e.g. work profile slideName) */
    selectedTab?: string;
    sx?: SxProps<Theme>;
    typographySx?: SxProps<Theme>;
    containerStyle?: React.CSSProperties;
    innerContainerStyle?: React.CSSProperties;
    innerContainerSx?: SxProps<Theme>;
    initialIndex?: number;
    highlights?: number[];
    enableButton?: boolean;
    buttonProps?: ICustomButton;
    disregardParam?: boolean;
    /** If a feld is required then highlight it with a red dot */
    requiredFields?: string[];
    /** Function to check if a field is filled/valid */
    isFieldFilled?: (fieldName: string) => boolean;
}

/** Matches Tailwind `border-surface-variant` / neutral rail */
const SURFACE_VARIANT_BORDER = '#262626';
/** ~text-neutral-400 */
const TAB_INACTIVE = '#a3a3a3';

const CustomSegmentedControl: React.FC<ICustomSegmentedControl> = ({
    onHandleClick,
    tabs,
    variant = 'default',
    selectedTab,
    sx,
    typographySx,
    containerStyle,
    highlights,
    initialIndex,
    enableButton,
    buttonProps,
    innerContainerStyle,
    innerContainerSx,
    disregardParam,
    requiredFields = [],
    isFieldFilled,
}) => {
    const { themeStyle } = useThemeContext();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [searchParams, setSearchParam] = useSearchParams();

    useEffect(() => {
        if (selectedTab === undefined) return;
        const idx = tabs.indexOf(selectedTab);
        if (idx >= 0) setCurrentIndex(idx);
    }, [selectedTab, tabs]);

    const handlePress = (tab: string, index: number) => {
        onHandleClick(tab, index);
        setCurrentIndex(index);
        if (!disregardParam) {
            setSearchParam({
                tab,
            });
        }
    };

    const handleSetTab = useCallback(() => {
        const params = Object.fromEntries([...searchParams]);
        if (params && params['tab']) {
            const tabIndex = tabs.findIndex((t, i) => t === params['tab']);
            if (tabIndex >= 0) {
                onHandleClick(params['tab'], tabIndex);
                setCurrentIndex(tabIndex);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    useEffect(() => {
        if (initialIndex) {
            const tabName = tabs.find((_, i) => i === initialIndex);
            if (tabName) {
                onHandleClick(tabName, initialIndex);
            }
            setCurrentIndex(initialIndex);
        }
        handleSetTab();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const isUnderline = variant === 'underline';

    return (
        <Box
            style={{
                alignItems: 'center',
                width: '100%',
                backgroundColor: isUnderline ? 'transparent' : theme[themeStyle].pure_black,
                ...containerStyle,
            }}
            sx={{
                position: isUnderline ? 'static' : { xs: 'absolute', sm: 'static' },
                top: isUnderline ? undefined : '135px',
                display: {
                    xs: 'block',
                    sm: 'flex',
                },
                left: 0,
                right: 0,
                justifyContent: isUnderline ? 'flex-start' : { xs: 'flex-start', sm: 'flex-start' },
                paddingLeft: isUnderline ? 0 : { xs: '20px', sm: '70px' },
                paddingRight: isUnderline ? 0 : undefined,
                boxShadow: isUnderline ? 'none' : `0 1.5px 2px -2px ${theme[themeStyle].placeholderGray}`,
                /* Underline variant: flat bar + bottom rule only (no grey fill). */
                borderBottom: isUnderline ? `1px solid ${SURFACE_VARIANT_BORDER}` : `1px ${theme[themeStyle].placeholderGray}`,
                bgcolor: isUnderline ? 'transparent' : undefined,
                overflowX: isUnderline ? 'auto' : 'scroll',
                scrollbarWidth: isUnderline ? 'none' : undefined,
                '&::-webkit-scrollbar': isUnderline ? { display: 'none' } : undefined,
                height: {
                    xs: isUnderline ? 'auto' : '60px',
                },
                minHeight: isUnderline ? 48 : undefined,
                ...sx,
            }}>
            <Box
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                    position: 'relative',
                    ...innerContainerStyle,
                }}>
                <Box
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    sx={{
                        gap: isUnderline ? { xs: 2.5, sm: 4 } : { xs: '20px', sm: '40px' },
                        width: {
                            xs: '100%',
                            sm: 'fit-content',
                        },
                        height: '100%',
                        justifyContent: isUnderline ? 'flex-start' : 'center',
                        flexWrap: isUnderline ? 'nowrap' : 'wrap',
                        ...innerContainerSx,
                    }}>
                    {tabs.map((tab, index) => {
                        const isRequired = requiredFields.includes(tab);
                        const isFilled = isFieldFilled ? isFieldFilled(tab) : false;
                        const showErrorState = isRequired && !isFilled;

                        return (
                            <Button
                                onClick={() => handlePress(tab, index)}
                                style={{
                                    backgroundColor: theme[themeStyle].transparent,
                                    height: '100%',
                                    width: 'fit-content',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: 0,
                                    position: 'relative',
                                }}
                                key={index}
                                sx={{
                                    marginTop: isUnderline ? 0 : 2.5,
                                    py: isUnderline ? 0 : 0,
                                    pt: isUnderline ? 0 : undefined,
                                    pb: isUnderline ? 2 : undefined,
                                    px: isUnderline ? 0 : undefined,
                                    flex: isUnderline ? { xs: 1, sm: '0 0 auto' } : undefined,
                                    textTransform: 'none',
                                    borderBottom: isUnderline
                                        ? currentIndex === index
                                            ? `2px solid ${theme[themeStyle].highlight}`
                                            : '2px solid transparent'
                                        : currentIndex === index
                                        ? `1px solid ${theme[themeStyle].primary_orange}`
                                        : 'none',
                                    borderRadius: 0,
                                    transition: 'border-color 0.2s ease, color 0.2s ease',
                                    minWidth: isUnderline ? 'unset' : '150px',
                                    '&:hover': isUnderline
                                        ? {
                                              backgroundColor: 'transparent',
                                              '& .MuiTypography-root': {
                                                  color:
                                                      currentIndex === index
                                                          ? theme[themeStyle].highlight
                                                          : '#f5f5f5',
                                              },
                                          }
                                        : undefined,
                                }}>
                                <Typography
                                    style={{
                                        textAlign: isUnderline ? 'left' : 'center',
                                        fontSize: isUnderline ? 15 : 14,
                                        fontStyle: 'normal',
                                        fontWeight: isUnderline ? (currentIndex === index ? 700 : 500) : 600,
                                        fontFamily: isUnderline ? FONT_ENUM.BOLD_HEADER : FONT_ENUM.MAIN_BOLD,
                                        textTransform: isUnderline ? 'none' : 'capitalize',
                                    }}
                                    sx={{
                                        textAlign: isUnderline ? { xs: 'center', sm: 'left' } : undefined,
                                        color: isUnderline
                                            ? currentIndex === index
                                                ? theme[themeStyle].highlight
                                                : TAB_INACTIVE
                                            : currentIndex === index
                                            ? theme[themeStyle].primary_white
                                            : theme[themeStyle].form_label,
                                        transition: 'color 0.2s ease',
                                        width: 'fit-content',
                                        position: 'relative',
                                        letterSpacing: isUnderline ? '-0.01em' : undefined,
                                        ...typographySx,
                                    }}>
                                    {tab}
                                    {highlights?.includes(index) && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                height: 4,
                                                width: 4,
                                                borderRadius: 4,
                                                backgroundColor: theme[themeStyle].highlight,
                                                top: 2,
                                                right: -5,
                                            }}
                                        />
                                    )}
                                    {isRequired && (
                                        <StyledBadge
                                            errorState={showErrorState}
                                            overlap="circular"
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                            variant="dot"
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: -10,
                                                zIndex: 1,
                                            }}
                                        />
                                    )}
                                </Typography>
                            </Button>
                        );
                    })}
                </Box>
                {!!enableButton && (
                    <CustomButton
                        label="Submit"
                        customStyle={{ position: 'absolute', right: 10, top: '10%', width: '100px' }}
                        {...buttonProps}
                    />
                )}
            </Box>
        </Box>
    );
};

CustomSegmentedControl.defaultProps = {
    tabs: ['tab1', 'tab2', 'tab3'],
};

export { CustomSegmentedControl };
